import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import ProductApi from "../../services/ProductApi";
import FamilyApi from "../../services/FamilyApi";

// Design System
import DataTable from "../../components/DataTable/DataTable";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import { Tag } from "../../components/Tag/Tag";
import PageHeader from "../../components/PageHeader/PageHeader";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

interface Product {
  id: string;
  partNumber: string;
  name: string;
  description: string;
  familyId: string;
  family?: string;
  isProducible: boolean;
}
interface Family {
  id: string;
  name: string;
}

const STORAGE_KEY = "ListProducts_filters";

const ListProducts: React.FC = () => {
  const { plantId } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);

  // Filter States
  const [filterPartNumber, setFilterPartNumber] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterDescription, setFilterDescription] = useState("");
  const [filterFamily, setFilterFamily] = useState<string[]>([]);

  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");

  useEffect(() => {
    fetchFamilies();
  }, []);

  useEffect(() => {
    if (families.length > 0 || !isLoading) {
      if (plantId) fetchProducts(savedFilters, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId, families.length]);

  const fetchFamilies = async (): Promise<void> => {
    try {
      const response = await FamilyApi.GetAllFamilies();
      setFamilies(response || []);
    } catch {
      console.error("Erro ao buscar famílias");
    }
  };

  const fetchProducts = async (
    filters: Record<string, any> = {},
    firstIndex = 0,
    field = sortField,
    direction: "asc" | "desc" = sortOrder,
    pageSize: number = rows,
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const partNumber = filters.partNumber?.value || undefined;
      const name = filters.name?.value || undefined;
      const description = filters.description?.value || undefined;
      const familyIds: string[] = filters.family?.value || [];

      const response = await ProductApi.GetProductsByFilterAsync(
        Math.floor(firstIndex / pageSize) + 1,
        pageSize,
        field,
        direction,
        partNumber,
        name,
        description,
        familyIds,
        undefined,
        plantId ? [plantId] : [],
      );

      const dataRaw = Array.isArray(response.data?.products) ? response.data.products : [];
      const data = dataRaw.map((product: Product) => {
        const matchedFamily = families.find((f) => f.id === product.familyId);
        return {
          ...product,
          family: matchedFamily?.name || "N/A",
        };
      });

      setProducts(data);
      setTotalRecords(response.data.total || data.length);
    } catch {
      displayToast("error", translateForm("error"), "Erro ao carregar produtos.", false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      partNumber: filterPartNumber ? { value: filterPartNumber } : undefined,
      name: filterName ? { value: filterName } : undefined,
      description: filterDescription ? { value: filterDescription } : undefined,
      family: filterFamily.length > 0 ? { value: filterFamily } : undefined,
    };

    Object.keys(filters).forEach((key) => {
      if (filters[key] === undefined) delete filters[key];
    });

    setPage(0);
    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    fetchProducts(filters, 0);
  };

  const handleClearFilters = (): void => {
    setFilterPartNumber("");
    setFilterName("");
    setFilterDescription("");
    setFilterFamily([]);

    setPage(0);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    fetchProducts({}, 0);
  };

  const handlePage = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
    fetchProducts(savedFilters, e.first, sortField, sortOrder, e.rows);
  };

  const handleSort = (e: any): void => {
    const dir = e.sortOrder === 1 ? "asc" : "desc";
    setSortField(e.sortField);
    setSortOrder(dir);
    fetchProducts(savedFilters, page, e.sortField, dir);
  };

  const deleteProduct = async (id: string): Promise<void> => {
    try {
      await ProductApi.DeleteProduct(id);
      setProducts((prev) => prev.filter((f) => f.id !== id));
      displayToast("success", translateForm("productSuceffullyDeleted"), "", false, 3000);
    } catch (err: any) {
      const apiErrors = err?.data?.data;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        const messages = apiErrors.map((er: any) => `• ${er.data?.message || er.message}`).join("\n");
        displayToast("error", translateForm("productNotDeleted"), messages, false, 5000);
      } else {
        const fallbackMsg = err?.data?.data ?? translateForm("productNotDeleted");
        displayToast("error", translateForm("productNotDeleted"), fallbackMsg, false, 5000);
      }
    }
  };

  const confirmDelete = (id: string): void => {
    setProductToDelete(id);
    setShowConfirmDelete(true);
  };



  const truncateWithTooltip = (text: string, limit: number = 30): React.ReactNode => {
    if (!text) return "";
    const truncated = text.length > limit ? text.slice(0, limit) + "..." : text;
    return <span title={text}>{truncated}</span>;
  };

  const actionBodyTemplate = (rowData: Product): JSX.Element => (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <IconButton
        icon="visibility"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/products/${rowData.id}`)}
        aria-label={translateGeneral("seeDetails")}
      />
      <IconButton
        icon="edit"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/products/${rowData.id}/edit`)}
        aria-label={translateGeneral("edit")}
      />
      <IconButton
        icon="delete"
        variant="text"
        severity="secondary"
        onClick={() => confirmDelete(rowData.id)}
        aria-label={translateGeneral("delete")}
      />
    </div>
  );

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("products")}
        description="Gerencie os produtos do sistema"
      >
        <Button
          label={translateForm("register")}
          icon="add"
          severity="primary"
          onClick={() => navigate("/products/create")}
        />
      </PageHeader>

      <div>
        <ConfirmModal
          visible={showConfirmDelete}
          onHide={() => {
            setShowConfirmDelete(false);
            setProductToDelete(null);
          }}
          onConfirm={() => productToDelete && deleteProduct(productToDelete)}
          title={translateGeneral("confirmation")}
          message={translateForm("productDeleteConfirmMessage")}
          severity="danger"
          confirmLabel={translateForm("Yes")}
          cancelLabel={translateForm("No")}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <FilterPanel
            title="Filtros"
            showFilterIcon
            onApply={handleFilter}
            onClear={handleClearFilters}
          >
            <Input
              type="text"
              label={translateForm("partNumber")}
              placeholder="Digite o part number"
              value={filterPartNumber}
              onChange={(val: any) => setFilterPartNumber(val)}
            />
            <Input
              type="text"
              label={translateForm("name")}
              placeholder="Digite o nome"
              value={filterName}
              onChange={(val: any) => setFilterName(val)}
            />
            <Input
              type="text"
              label={translateForm("description")}
              placeholder="Digite a descrição"
              value={filterDescription}
              onChange={(val: any) => setFilterDescription(val)}
            />
            <Input
              type="multiselect"
              label={translateForm("family")}
              placeholder="Selecione as famílias"
              value={filterFamily}
              onChange={(val: any) => setFilterFamily(val)}
              options={families.map((f) => ({ label: f.name, value: f.id, id: f.id }))}
            />
          </FilterPanel>

          <DataTable
            value={products}
            paginated
            first={page}
            rows={rows}
            totalRecords={totalRecords}
            onPageChange={handlePage}
            rowsPerPageOptions={[10, 20, 50]}
            loading={isLoading}
            emptyMessage={translateForm("noRecordsFound") || "Nenhum registro encontrado"}
            sortField={sortField}
            sortOrder={sortOrder === "asc" ? 1 : -1}
            onSort={handleSort}
            resizableColumns
          >
            <Column
              field="partNumber"
              header={translateForm("partNumber")}
              sortable
              style={{ minWidth: "10rem" }}
            />
            <Column
              field="name"
              header={translateForm("name")}
              sortable
              body={(rowData: Product) => truncateWithTooltip(rowData.name)}
            />
            <Column
              field="description"
              header={translateForm("description")}
              sortable
              body={(rowData: Product) => truncateWithTooltip(rowData.description)}
            />
            <Column
              field="family"
              header={translateForm("family")}
              sortable
              style={{ minWidth: "8rem" }}
              body={(rowData: Product) => rowData.family || "N/A"}
            />
            <Column
              field="isProducible"
              header={translateForm("status")}
              sortable
              style={{ width: "8rem" }}
              body={(rowData: Product) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Tag
                    label={rowData.isProducible ? translateForm("active") : translateForm("inactive")}
                    severity={rowData.isProducible ? "successInverse" : "errorInverse"}
                  />
                </div>
              )}
            />
            <Column
              field="actions"
              header={translateForm("actions")}
              body={actionBodyTemplate}
              style={{ width: "7rem" }}
              resizeable={false}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default ListProducts;
